// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  ChangeLogEvent,
  validateChangeLogEvent
} from '../../../state_manager/data_models/change_log_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export type ChangeLogContextTable = 'accounts' | 'deals' | 'contacts' | 'prospects' | 'funds' | 'securities'

export interface ChangeLogPage {
  data: ChangeLogEvent[]
  hasMore: boolean
  count: number
  error?: string
}

export const fetchChangeLog = async (
  contextTable: ChangeLogContextTable,
  recordId: string,
  page: number,
  pageSize: number,
  accessToken: string | null = null
): Promise<ChangeLogPage> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CHANGE_LOG.LIST, { contextTable, recordId }),
      {
        method: API_ROUTES.CHANGE_LOG.LIST.method,
        headers,
        body: JSON.stringify({ data: { page, page_size: pageSize } })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch change log: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as {
      data?: unknown
      has_more?: boolean
      count?: number
    }
    const rows = Array.isArray(json.data) ? json.data : []

    // Validate before it reaches the tab (a response that fails validation
    // is an error, not something to coerce — CLAUDE.md).
    const validated = rows.map(validateChangeLogEvent)

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      count: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching change log:', error)
    }
    return {
      data: [],
      hasMore: false,
      count: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching the change log'
    }
  }
}
